import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { Container } from '@vnetwork/ui/layout';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@vnetwork/ui/primitives';
import { Alert } from '@vnetwork/ui/feedback';
import { useOpsOrg } from '@/api/operations-org-hooks';
import { useOpsServices } from '@/api/operations';
import { startImpersonation, ImpersonationError } from '@/api/impersonation';
import {
  getImpersonationSessionForOrg,
  type ImpersonationSession,
} from '@/lib/impersonation-session';
import { setImpersonationOrgId } from '@/api/impersonation-client';
import { TabError, TabLoading } from '@/pages/customers/detail/tab-states';
import { OpsOrgDetailHeader } from './ops-org-detail-header';
import { OpsOrgOverviewTab } from './tabs/ops-org-overview-tab';
import { OpsOrgTeamsTab } from './tabs/ops-org-teams-tab';
import { OpsOrgProjectsTab } from './tabs/ops-org-projects-tab';
import { OpsOrgServicesTab } from './tabs/ops-org-services-tab';
import { OpsOrgCdnDomainsTab } from './tabs/ops-org-cdn-domains-tab';
import { OpsOrgWaapDomainsTab } from './tabs/ops-org-waap-domains-tab';

const TABS = ['overview', 'teams', 'projects', 'services', 'cdn', 'waap'] as const;
type TabValue = (typeof TABS)[number];
const DEFAULT_TAB: TabValue = 'overview';

/**
 * /operations/orgs/:orgId — org detail.
 *
 * Implicit customer token: on mount the page silently mints (or reuses) the
 * org's customer JWT via DC /partner/auth/impersonate and sets it as the active
 * org in impersonation-client. CDN and WAAP tabs use this token automatically.
 *
 * Graceful degradation: if mint fails (internal org 8064, no DC contact, etc.)
 * the CDN/WAAP tabs show an inline notice instead of crashing.
 *
 * Tabs:
 *   Overview  — customer info + KPI stats + category breakdown
 *   Teams     — team list with member counts
 *   Projects  — project list with resource counts
 *   Services  — services filtered to this org → service detail
 *   CDN       — org-wide CDN domain list (customer token, → CDN console)
 *   WAAP      — WAAP website list for first active WAAP service (→ WAAP console)
 */
export default function OperationsOrgDetailPage() {
  const { orgId = '' } = useParams<{ orgId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get('tab') ?? '';
  const activeTab: TabValue = (TABS as readonly string[]).includes(rawTab)
    ? (rawTab as TabValue)
    : DEFAULT_TAB;

  const setTab = (tab: string) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.set('tab', tab);
      return next;
    });
  };

  const { data, isLoading, isError, refetch } = useOpsOrg(orgId);

  // Fetch WAAP services for this org so we can pass serviceId to the WAAP tab.
  const waapSvcsQ = useOpsServices({ org_id: Number(orgId), category: 'waap', per_page: 5 });
  const waapServiceId = waapSvcsQ.data?.results?.[0]?.id ?? '';

  // ── Silent customer-token mint ───────────────────────────────────────────
  // Track mint state: 'idle' → 'minting' → 'done' | 'no-contact' | 'error'
  const [mintState, setMintState] = useState<
    'idle' | 'minting' | 'done' | 'no-contact' | 'error'
  >(() => {
    // If already cached from a previous visit this session, skip minting.
    return getImpersonationSessionForOrg(orgId) ? 'done' : 'idle';
  });

  const [_session, setSession] = useState<ImpersonationSession | null>(
    () => getImpersonationSessionForOrg(orgId),
  );

  useEffect(() => {
    // Set the active org so impersonation-client picks the right cached token.
    setImpersonationOrgId(orgId);

    // Mint only when not already cached.
    if (mintState !== 'idle') return;
    if (!orgId || !data) return; // wait for org data to get the display name

    const displayName =
      data.customer?.customer_company_name ||
      data.customer?.customer_name ||
      `Organization ${data.organization_id}`;

    const customerId = data.customer?.customer_id;
    if (!customerId) {
      // Internal org with no DC customer record — can't impersonate.
      setMintState('no-contact');
      return;
    }

    setMintState('minting');
    startImpersonation(customerId, displayName)
      .then((session) => {
        setSession(session);
        setMintState('done');
      })
      .catch((err) => {
        if (err instanceof ImpersonationError && err.code === 'NO_CONTACT') {
          setMintState('no-contact');
        } else {
          setMintState('error');
        }
      });
  }, [orgId, data, mintState]);

  // Re-set active org if navigating between org pages without unmounting.
  // No cleanup on unmount — console pages (cdn/waap detail) re-set the org from
  // their own session read, so clearing here would race with their mount.
  useEffect(() => {
    setImpersonationOrgId(orgId);
  }, [orgId]);

  // Whether the customer token is available for CDN/WAAP tabs.
  const hasCustomerToken = mintState === 'done' && Boolean(_session);
  const hasCDN =
    Boolean(data?.services.by_category?.['cdn']) &&
    Object.values(data?.services.by_category?.['cdn'] ?? {}).some((n) => n > 0);
  const hasWAAP =
    Boolean(waapServiceId) &&
    Boolean(data?.services.by_category?.['waap']) &&
    Object.values(data?.services.by_category?.['waap'] ?? {}).some((n) => n > 0);

  // ── Render guards ────────────────────────────────────────────────────────
  if (isError) {
    return (
      <Container size="full">
        <TabError title="Failed to load organization" onRetry={() => refetch()} />
      </Container>
    );
  }
  if (isLoading || !data) {
    return (
      <Container size="full">
        <TabLoading rows={4} />
      </Container>
    );
  }

  // Inline notice shown inside CDN/WAAP tabs when mint failed.
  const noTokenNotice =
    mintState === 'no-contact' ? (
      <Alert tone="neutral" title="Org này không có tài khoản khách hàng để xem dữ liệu dịch vụ">
        Internal organizations or orgs without a Keycloak contact cannot be impersonated.
      </Alert>
    ) : mintState === 'error' ? (
      <Alert tone="warning" title="Failed to load customer token">
        Try refreshing the page.
      </Alert>
    ) : null;

  return (
    <Container size="full">
      <div style={{ display: 'grid', gap: 'var(--vn-space-4)' }}>
        <OpsOrgDetailHeader data={data} session={mintState === 'done' ? _session : null} />

        <Tabs value={activeTab} onValueChange={setTab}>
          <TabsList>
            <TabsTrigger value="overview">Overview</TabsTrigger>
            <TabsTrigger value="teams">
              Teams{data.teams_count > 0 ? ` (${data.teams_count})` : ''}
            </TabsTrigger>
            <TabsTrigger value="projects">
              Projects{data.projects_count > 0 ? ` (${data.projects_count})` : ''}
            </TabsTrigger>
            <TabsTrigger value="services">
              Services{data.services.total > 0 ? ` (${data.services.total})` : ''}
            </TabsTrigger>
            {hasCDN && <TabsTrigger value="cdn">CDN</TabsTrigger>}
            {hasWAAP && <TabsTrigger value="waap">WAAP</TabsTrigger>}
          </TabsList>

          <TabsContent value="overview">
            <OpsOrgOverviewTab data={data} />
          </TabsContent>

          <TabsContent value="teams">
            {activeTab === 'teams' && <OpsOrgTeamsTab orgId={orgId} />}
          </TabsContent>

          <TabsContent value="projects">
            {activeTab === 'projects' && <OpsOrgProjectsTab orgId={orgId} />}
          </TabsContent>

          <TabsContent value="services">
            {activeTab === 'services' && <OpsOrgServicesTab orgId={orgId} />}
          </TabsContent>

          {hasCDN && (
            <TabsContent value="cdn">
              {activeTab === 'cdn' && (
                hasCustomerToken
                  ? <OpsOrgCdnDomainsTab orgId={orgId} />
                  : noTokenNotice ?? <TabLoading rows={3} />
              )}
            </TabsContent>
          )}

          {hasWAAP && (
            <TabsContent value="waap">
              {activeTab === 'waap' && (
                hasCustomerToken
                  ? <OpsOrgWaapDomainsTab serviceId={waapServiceId} />
                  : noTokenNotice ?? <TabLoading rows={3} />
              )}
            </TabsContent>
          )}
        </Tabs>
      </div>
    </Container>
  );
}
